import React, { useEffect, useState } from "react"; // ← rend le fichier module
import { supabase } from "../supabaseClient";
import { Save, Camera } from "lucide-react";
import i18n from "../i18n";

// Mini hook i18n "safe" (même pattern que le reste de l'app)
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string): string => String((i18n.t as any)(key, { lng }));
  return { t };
}

function ProfileOnboardingModal({ onDone }: { onDone: () => void }) {
  const { t } = useI18nSafe();

  const [full_name, setFullName] = useState("");
  const [club, setClub] = useState("");
  const [niveau, setNiveau] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  // 👇 NEW
  const [pseudo, setPseudo] = useState("");
  const [publicUsePseudo, setPublicUsePseudo] = useState(false);
  const [pseudoErr, setPseudoErr] = useState<string>("");

  const [saving, setSaving] = useState(false);

  const isValidPseudo = (v: string) => /^[A-Za-z0-9_.-]{3,32}$/.test(v);

  const save = async () => {
    setPseudoErr("");

    // validations
    if (!full_name.trim()) return; // bouton gère déjà le disabled

    if (publicUsePseudo) {
      if (!pseudo.trim()) {
        setPseudoErr(t("onboarding:errors.pseudo_required"));
        return;
      }
      if (!isValidPseudo(pseudo.trim())) {
        setPseudoErr(t("onboarding:errors.pseudo_invalid"));
        return;
      }
    } else if (pseudo.trim() && !isValidPseudo(pseudo.trim())) {
      setPseudoErr(t("onboarding:errors.pseudo_invalid"));
      return;
    }

    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }

    let photo_url = "";

    if (photoFile) {
      const ext = photoFile.name.split(".").pop();
      const filePath = `${user.id}/avatar.${ext}`;
      const { error: upErr } = await supabase
        .storage.from("avatars")
        .upload(filePath, photoFile, { upsert: true });
      if (!upErr) photo_url = filePath;
    }

    const payload = {
      id: user.id,
      full_name: full_name.trim(),
      club: club.trim(),
      niveau,
      photo_url,
      // 👇 NEW
      pseudo: pseudo.trim() || null,
      public_use_pseudo: publicUsePseudo,
    };

    const { error } = await supabase.from("user_profiles").upsert(payload);

    setSaving(false);
    if (error) {
      console.error(error);
      if ((error as any).code === "23505" && String((error as any).message || "").toLowerCase().includes("pseudo")) {
        setPseudoErr(t("onboarding:errors.pseudo_taken"));
      } else {
        alert(t("onboarding:errors.save_error"));
      }
      return;
    }
    onDone();
  };

  const canSubmit =
    !!full_name.trim() &&
    !saving &&
    (!publicUsePseudo || (pseudo.trim().length >= 3 && isValidPseudo(pseudo.trim())));

  const publicDisplayName =
    publicUsePseudo && pseudo.trim() ? pseudo.trim() : (full_name.trim() || "—");

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-2xl">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold">{t("onboarding:title")}</h3>
            <p className="text-sm text-gray-500 mt-1">
              {t("onboarding:subtitle")}
            </p>
          </div>

          <div className="p-5 space-y-5">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-800 grid place-items-center overflow-hidden">
                {photoFile ? (
                  <img
                    src={URL.createObjectURL(photoFile)}
                    alt={t("onboarding:photo_alt")}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Camera className="w-6 h-6 text-gray-500" aria-hidden="true" />
                )}
              </div>
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                <Camera className="w-4 h-4" />
                <span>{t("onboarding:add_photo")}</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm">{t("onboarding:full_name_label")}</label>
                <input
                  value={full_name}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t("onboarding:full_name_placeholder")}
                  className="mt-1 w-full rounded-lg border px-3 py-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                />
              </div>
              <div>
                <label className="text-sm">{t("onboarding:club_label")}</label>
                <input
                  value={club}
                  onChange={(e) => setClub(e.target.value)}
                  placeholder={t("onboarding:club_placeholder")}
                  className="mt-1 w-full rounded-lg border px-3 py-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm">{t("editprofile:fields.level.label")}</label>
                <select
                  value={niveau}
                  onChange={(e) => setNiveau(e.target.value)}
                  className="mt-1 w-full rounded-lg border px-3 py-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                >
                  <option value="">{t("editprofile:fields.level.placeholder")}</option>
                  <option value="Départemental">{t("editprofile:fields.level.options.departemental")}</option>
                  <option value="Régional">{t("editprofile:fields.level.options.regional")}</option>
                  <option value="National">{t("editprofile:fields.level.options.national")}</option>
                  <option value="Elite">{t("editprofile:fields.level.options.elite")}</option>
                </select>
              </div>
            </div>

            {/* 👇 NEW — Pseudo + préférence d’affichage */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm">{t("onboarding:pseudo_label")}</label>
                <input
                  value={pseudo}
                  onChange={(e) => setPseudo(e.target.value)}
                  placeholder={t("onboarding:pseudo_placeholder")}
                  maxLength={32}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 ${
                    pseudoErr ? "border-red-500" : ""
                  }`}
                />
                <div className="mt-1 flex items-center justify-between text-xs">
                  <span className={`${pseudoErr ? "text-red-600" : "text-gray-500 dark:text-gray-400"}`}>
                    {t("onboarding:pseudo_hint")}
                  </span>
                  <span className="text-gray-400">{pseudo.length}/32</span>
                </div>
                {pseudoErr && <p className="mt-1 text-xs text-red-600">{pseudoErr}</p>}
              </div>

              <div className="sm:pt-6">
                <label className="inline-flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={publicUsePseudo}
                    onChange={(e) => setPublicUsePseudo(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm">
                    {t("onboarding:pseudo_toggle_prefix")} <strong>{t("onboarding:pseudo_word")}</strong>{" "}
                    {t("onboarding:pseudo_toggle_middle")} <strong>{t("onboarding:name_word")}</strong>
                  </span>
                </label>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t("onboarding:public_name_label")} <span className="font-medium">{publicDisplayName}</span>
                </p>
              </div>
            </div>
          </div>

          <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-800 flex justify-end">
            <button
              onClick={save}
              disabled={!canSubmit}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded text-white ${
                !canSubmit ? "bg-orange-300" : "bg-orange-600 hover:bg-orange-700"
              }`}
            >
              <Save className="w-4 h-4" />
              {saving ? t("onboarding:saving") : t("onboarding:submit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileOnboardingModal;
